import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Ban, Palette, Pipette, Droplets, Sparkles, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { SCHEME_PRESETS, schemeVariants, STANDARD_COLORS, getRecentColors, pushRecentColor, normalizeHex } from '../../lib/colors';
import { startSampling } from '../../lib/colorSampler';
import { Monitor } from 'lucide-react';
import { useEditor } from '../../context/EditorContext';

const hover = 'hover:bg-[hsl(var(--wp-command-hover))]';

/**
 * Publisher-style colour picker popover.
 * value: hex | null (no fill). onChange(hex|null)
 */
export const ColorPicker = ({ value, onChange, allowNoFill = true, children, label = 'Color', align = 'start', onFillEffects, testId = 'color-picker', extras = [] }) => {
  const { state, openDialog } = useEditor();
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState(null);
  const [recent, setRecent] = useState(getRecentColors());
  const scheme = SCHEME_PRESETS[state.schemeId] || SCHEME_PRESETS.office;
  const columns = useMemo(() => scheme.map((c) => ({ ...c, variants: schemeVariants(c.hex) })), [scheme]);

  const close = () => { setOpen(false); setPanel(null); };
  const pick = (hex) => {
    const n = hex ? normalizeHex(hex) : null;
    onChange(n);
    if (n) setRecent(pushRecentColor(n));
    close();
  };

  const swatch = (hex, key, name, sel) => (
    <button
      key={key}
      type="button"
      title={name || hex}
      onClick={() => pick(hex)}
      className={`h-[18px] w-[18px] rounded-[2px] border border-black/10 dark:border-white/20 hover:scale-[1.15] transition-transform duration-100 ${sel ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : ''}`}
      style={{ background: hex }}
      data-testid={`color-swatch-${key}`}
    />
  );
  const cur = value ? normalizeHex(value) : null;

  const applySampled = (hex) => { const n = normalizeHex(hex); onChange(n); setRecent(pushRecentColor(n)); };
  /** Sample from the page: in-app eyedropper over the canvas (pictures, gradients, text — any browser). */
  const samplePage = () => { close(); startSampling({ onPick: applySampled, label: label === 'Color' ? 'Fill Color' : `${label} Color` }); };
  /** Sample from anywhere on screen (browser EyeDropper API — Chrome / Edge). */
  const eyedropper = async () => {
    if (!window.EyeDropper) return;
    try {
      const res = await new window.EyeDropper().open();
      pick(res.sRGBHex);
    } catch { /* cancelled */ }
  };

  const activePanel = panel ? extras.find((x) => x.id === panel) : null;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPanel(null); }} modal={false}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} collisionPadding={8} className="w-[236px] p-2 bg-popover text-popover-foreground border border-border shadow-lg z-[90] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto" data-ribbon data-keep-editing onOpenAutoFocus={(e) => e.preventDefault()} data-testid={testId}>
        {activePanel ? (
          <div data-testid={`${testId}-panel-${activePanel.id}`}>
            <button type="button" onClick={() => setPanel(null)} className={`w-full flex items-center gap-2 px-1 py-1 mb-1.5 rounded-sm text-[12px] font-semibold ${hover}`} data-testid={`${testId}-panel-back`}>
              <ChevronLeft className="h-4 w-4 text-muted-foreground" /> {activePanel.label}
            </button>
            {activePanel.panel(close)}
          </div>
        ) : (
          <>
        <div className="text-[12px] font-semibold mb-1.5">Scheme Colors</div>
        <div className="grid grid-cols-8 gap-[3px] mb-1">{columns.map((c, i) => swatch(c.hex, `scheme-${i}`, c.name, cur === normalizeHex(c.hex)))}</div>
        <div className="grid grid-cols-8 gap-[3px]">
          {[0, 1, 2, 3, 4].map((row) => columns.map((c, i) => swatch(c.variants[row], `scheme-${i}-${row}`, `${c.name} ${row < 3 ? `lighter ${[80, 60, 40][row]}%` : `darker ${[25, 50][row - 3]}%`}`, cur === c.variants[row])))}
        </div>
        <div className="text-[12px] font-semibold mt-3 mb-1.5">Standard Colors</div>
        <div className="grid grid-cols-10 gap-[3px]">{STANDARD_COLORS.map((c, i) => swatch(c.hex, `standard-${i}`, c.name, cur === c.hex))}</div>
        {recent.length > 0 && (
          <>
            <div className="text-[12px] font-semibold mt-3 mb-1.5">Recent Colors</div>
            <div className="grid grid-cols-10 gap-[3px]">{recent.map((c, i) => swatch(c, `recent-${i}`, c, cur === c))}</div>
          </>
        )}
        <div className="border-t border-border mt-2 pt-1 space-y-px">
          {allowNoFill && (
            <button type="button" onClick={() => pick(null)} className={`w-full flex items-center gap-2 px-2 py-1 rounded-sm text-[13px] ${hover}`} data-testid="color-no-fill"><Ban className="h-4 w-4 text-muted-foreground" /> No {label === 'Color' ? 'Fill' : label}</button>
          )}
          <button type="button" onClick={() => { close(); openDialog('colors', { initial: cur || '#000000', onPick: (hex) => { onChange(hex); setRecent(pushRecentColor(hex)); } }, { stack: true }); }} className={`w-full flex items-center gap-2 px-2 py-1 rounded-sm text-[13px] ${hover}`} data-testid="color-more-colors"><Palette className="h-4 w-4 text-muted-foreground" /> More Colors...</button>
          <button type="button" onClick={() => { close(); openDialog('colors', { initial: cur || '#7c3aed', tab: 'custom', tints: true, onPick: (hex) => { onChange(hex); setRecent(pushRecentColor(hex)); } }, { stack: true }); }} className={`w-full flex items-center gap-2 px-2 py-1 rounded-sm text-[13px] ${hover}`} data-testid="color-tints"><Droplets className="h-4 w-4 text-muted-foreground" /> Tints...</button>
          <button type="button" onClick={samplePage} title="Pick a colour from any picture, shape or text on the page" className={`w-full flex items-center gap-2 px-2 py-1 rounded-sm text-[13px] ${hover}`} data-testid="color-sample"><Pipette className="h-4 w-4 text-muted-foreground" /> Sample {label === 'Color' ? 'Fill' : label} Color</button>
          {typeof window !== 'undefined' && window.EyeDropper && (
            <button type="button" onClick={eyedropper} title="Pick a colour from anywhere on your screen" className={`w-full flex items-center gap-2 px-2 py-1 rounded-sm text-[13px] ${hover}`} data-testid="color-sample-screen"><Monitor className="h-4 w-4 text-muted-foreground" /> Sample from Screen</button>
          )}
          {onFillEffects && (
            <button type="button" onClick={() => { close(); onFillEffects(); }} className={`w-full flex items-center gap-2 px-2 py-1 rounded-sm text-[13px] ${hover}`} data-testid="color-fill-effects"><Sparkles className="h-4 w-4 text-muted-foreground" /> Fill Effects...</button>
          )}
          {extras.map((x) => (
            <button
              key={x.id}
              type="button"
              onClick={() => { if (x.panel) setPanel(x.id); else { close(); x.onClick?.(); } }}
              className={`w-full flex items-center gap-2 px-2 py-1 rounded-sm text-[13px] ${hover}`}
              data-testid={x.testId || `color-extra-${x.id}`}
            >
              {x.icon && <x.icon className="h-4 w-4 text-muted-foreground" />}
              <span className="flex-1 text-left">{x.label}{x.panel ? '' : '...'}</span>
              {x.panel && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
            </button>
          ))}
        </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

/** Compact swatch trigger used in side panels/dialogs */
export const ColorField = ({ value, onChange, allowNoFill = true, label, testId }) => (
  <ColorPicker value={value} onChange={onChange} allowNoFill={allowNoFill} label={label || 'Color'} testId={testId ? `${testId}-picker` : undefined}>
    <button type="button" className="h-8 flex items-center gap-2 rounded-md border border-input bg-background px-2 text-xs text-foreground hover:bg-accent/30 w-full" data-testid={testId}>
      <span className="h-4 w-4 rounded-sm border border-border" style={value ? { background: value } : { backgroundImage: 'repeating-linear-gradient(45deg,#ccc 0 3px,#fff 3px 6px)' }} />
      <span className="flex-1 text-left truncate">{value ? value.toUpperCase() : 'No fill'}</span>
      <ChevronDown className="h-3 w-3 text-muted-foreground" />
    </button>
  </ColorPicker>
);
